import React, { useState, useEffect } from 'react';
import { format } from "date-fns";
import { 
    Box, 
    Button, 
    TextField, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    FormControlLabel, 
    RadioGroup, 
    Radio,
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import FlexBetween from 'components/FlexBetween';


const parseHHmm = (str) => {
    if (!str) return null;
    const [h, m] = str.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

const FlightFilterDialog = ({ 
    open, 
    onClose, 
    onApply, 
    acTypeOptions, 
    flightRegOptions, 
    currentFilters 
}) => {
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const [cleared, setCleared] = useState(false);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [cleared]);

    const initialFilterValues = {
        flightDate: null,
        flightNo: '',
        minEtd: null,
        maxEtd: null,
        dep: '',
        arr: '',
        acType: [],
        flightReg: [],
        isInvoiced: 'ALL',
    };

    const [filterValues, setFilterValues] = useState(() => {
        if (!currentFilters) return initialFilterValues;
        return {
            ...initialFilterValues,
            ...currentFilters,
            acType: currentFilters.acType ?? [],
            flightReg: currentFilters.flightReg ?? [],
            minEtd: parseHHmm(currentFilters.minEtd),
            maxEtd: parseHHmm(currentFilters.maxEtd),
        };
    });

    const handleDateChange = (date) => {
        setFilterValues(prev => ({ 
            ...prev, 
                flightDate: date 
        }));
    };

     const handleTimeChange = (field) => (time) => {
        setFilterValues(prev => ({ 
            ...prev, 
            [field]: time, // Date object
        }));
    };

    const handleFilterChange = (field) => (event) => {
        const value = event.target.value;
        setFilterValues(prev => ({
            ...prev,
            [field]: value, 
        }));
    };  

    const handleInvoicedChange = (event) => {
        setFilterValues(prev => ({
            ...prev,
            isInvoiced: event.target.value, 
        }));
    };

    const handleCancel = () => {
        setFilterValues(initialFilterValues);  
        onApply(null);
        onClose();  
    };

    const handleApply = () => {
        const appliedFilters = {
            ...filterValues,
            minEtd: filterValues.minEtd ? format(filterValues.minEtd, "HH:mm") : null,
            maxEtd: filterValues.maxEtd ? format(filterValues.maxEtd, "HH:mm") : null,
            acType: filterValues.acType.length ? filterValues.acType : null,
            flightReg: filterValues.flightReg.length ? filterValues.flightReg : null,
        };
        onApply(appliedFilters);
        onClose();
    };
    
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };


    return (   
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >     
            <Dialog 
                open={open} 
                onClose={handleCancel}
                sx={{ '& .MuiDialog-paper': { minWidth: { xs: '95%', sm: '40%' }} }}
            >
                <DialogTitle>
                    Filter
                </DialogTitle>
                <DialogContent onKeyDown={handleKeyPress}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FlexBetween
                            gap={2}
                            sx={{ marginTop: '6px' }}
                        >
                            <DatePicker
                                label="Flight Date"
                                value={filterValues.flightDate}
                                onChange={handleDateChange}
                                format="dd/MM/yyyy"
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                                sx={{ width: '50%' }}
                            />

                            <TextField
                                fullWidth
                                label="Flight No"
                                value={filterValues.flightNo}
                                onChange={handleFilterChange('flightNo')}
                                sx={{ width: '50%' }}
                            />
                        </FlexBetween>
                        
                        <FlexBetween gap={2}>                           
                            <TimePicker 
                                label="Min ETD"
                                views={['hours', 'minutes']} 
                                format="HH:mm"                                 
                                value={filterValues.minEtd}
                                onChange={handleTimeChange('minEtd')}
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />
                                     
                            <TimePicker 
                                label="Max ETD"
                                views={['hours', 'minutes']} 
                                format="HH:mm"                                 
                                value={filterValues.maxEtd}
                                onChange={handleTimeChange('maxEtd')}
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />        
                        </FlexBetween>
 
                        <FlexBetween gap={2}>         
                            <TextField
                                fullWidth
                                label="Departure"
                                value={filterValues.dep}
                                onChange={(event) => {
                                    const value = event.target.value.toUpperCase();
                                    setFilterValues((prev) => ({ ...prev, dep: value }));
                                }}
                            />                   
                            <TextField
                                fullWidth
                                label="Arrival"
                                value={filterValues.arr}
                                onChange={(event) => {
                                    const value = event.target.value.toUpperCase();
                                    setFilterValues((prev) => ({ ...prev, arr: value }));
                                }}
                            />                            
                        </FlexBetween>   

                        <FlexBetween gap={2}>                            
                            <FormControl fullWidth>
                                <InputLabel>AC Type</InputLabel>
                                <Select
                                    multiple
                                    label="AC Type"
                                    value={filterValues.acType}
                                    onChange={handleFilterChange('acType')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {acTypeOptions.map((type, index) => (
                                        <MenuItem key={index} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Flight Reg</InputLabel>
                                <Select
                                    multiple
                                    label="Flight Reg"
                                    value={filterValues.flightReg}
                                    onChange={handleFilterChange('flightReg')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {flightRegOptions.map((reg, index) => (
                                        <MenuItem key={index} value={reg}>
                                            {reg}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>                      
                        </FlexBetween>         

                        <Box>
                            <RadioGroup
                                value={filterValues.isInvoiced}
                                onChange={handleInvoicedChange}
                                row
                            >
                                <FormControlLabel value="ALL" control={<Radio />} label="All" />
                                <FormControlLabel value="INVOICED" control={<Radio />} label="Invoiced" />
                                <FormControlLabel value="NOT_INVOICED" control={<Radio />} label="Not Invoiced" />
                            </RadioGroup>
                        </Box>            
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancel} 
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={handleApply} 
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default FlightFilterDialog;